import { trackPageView } from "./analytics";
import AboutPage from "./pages/about/page";
import CareerPage from "./pages/career/page";
import ContactPage from "./pages/contact/page";
import EmployeesPage from "./pages/employees/page";
import HomePage from "./pages/home/page";
import NaasPage from "./pages/naas/page";
import OfferPage from "./pages/offer/page";
import TopBar from "./components/topbar";

import { Helmet } from "react-helmet";

import { useEffect } from "react";

import Footer1 from "./components/footer1";
import { SECTION_PATHS } from "./constants/site";
import { scrollToSection } from "./utils/scrollToSection";

const SECTION_IDS = Object.keys(SECTION_PATHS);
const PATH_TO_SECTION = Object.fromEntries(SECTION_IDS.map((id) => [SECTION_PATHS[id], id]));
const SPY_LINE = 0.4; // Sektionen som täcker en linje 40 % ned i fönstret räknas som aktuell

const normalizePath = (path) => path.replace(/\/+$/, "") || "/";

// Sista sektionen (uppifrån) vars överkant passerat linjen. Fungerar oavsett sektionens höjd,
// till skillnad från att kräva att en viss andel av sektionen syns.
const currentSection = () => {
  const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
  if (atBottom) return SECTION_IDS[SECTION_IDS.length - 1];
  const line = window.innerHeight * SPY_LINE;
  let current = SECTION_IDS[0];
  SECTION_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (el && el.getBoundingClientRect().top <= line) current = id;
  });
  return current;
};

function App() {
  useEffect(() => {
    // initGA(); // Initiera Google Analytics
    trackPageView(window.location.pathname + window.location.search); // Spåra startsida
  }, []);

  // Permalänkar: URL:en följer sektionen man scrollat till (/offer, /team ...) och en direktlänk
  // till en sektion landar på den. Vi använder replaceState så att webbläsarens bakåtknapp
  // inte behöver klickas igenom varje sektion man scrollat förbi.
  useEffect(() => {
    const pathId = PATH_TO_SECTION[normalizePath(window.location.pathname)];
    const hashId = window.location.hash.slice(1); // äldre delade länkar, t.ex. /#career
    const legacyId = Object.prototype.hasOwnProperty.call(SECTION_PATHS, hashId) ? hashId : null;
    const landingId = !pathId || pathId === "home" ? legacyId ?? pathId : pathId;
    const needsLanding = landingId && landingId !== "home";

    // Tills landningen är klar får spyn inte skriva över URL:en
    let spying = !needsLanding;
    let frame = 0;

    const replacePath = (id) => {
      window.history.replaceState(window.history.state, "", SECTION_PATHS[id] + window.location.search);
    };

    const syncUrl = () => {
      frame = 0;
      if (!spying) return;
      const id = currentSection();
      if (window.location.pathname !== SECTION_PATHS[id] || window.location.hash) {
        replacePath(id);
      }
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(syncUrl);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    // Landning: hoppa till sektionen direkt och håll den på plats medan bilder och typsnitt laddas
    // (de ändrar sidans höjd), tills sidan är färdigladdad eller besökaren scrollar själv. Vi väntar
    // alltså inte på load innan vi landar. Sidan väger flera MB, så på en vanlig uppkoppling skulle
    // besökaren annars stå på sidans topp i flera sekunder. Bara scrollavsikt avbryter (hjul, svep,
    // tangenter, drag i scrollbaren), inte t.ex. ett klick på cookiebannern.
    let stopLanding = () => {};
    if (needsLanding) {
      const settle = () => scrollToSection(landingId);
      const observer = new ResizeObserver(settle);
      const scrollIntents = ["wheel", "touchmove", "keydown"];
      let giveUp = 0;

      const finish = () => {
        spying = true;
        observer.disconnect();
        clearTimeout(giveUp);
        window.removeEventListener("load", onLoad);
        window.removeEventListener("pointerdown", onPointerDown);
        scrollIntents.forEach((type) => window.removeEventListener(type, finish));
      };
      const onLoad = () => {
        settle(); // layouten är nu slutgiltig
        finish();
      };
      const onPointerDown = (event) => {
        if (event.target === document.documentElement) finish(); // klick på scrollbaren
      };

      settle();
      replacePath(landingId);
      observer.observe(document.getElementById("root"));
      scrollIntents.forEach((type) => window.addEventListener(type, finish, { passive: true }));
      window.addEventListener("pointerdown", onPointerDown, { passive: true });
      if (document.readyState === "complete") {
        finish();
      } else {
        window.addEventListener("load", onLoad);
        giveUp = setTimeout(finish, 20000); // load kan utebli om någon resurs hänger sig
      }
      stopLanding = finish;
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      stopLanding();
    };
  }, []);

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Acebit - Vi är bäst i Sverige på att designa, bygga och arbeta med moderna nätverk."
        />
        <title>
          Acebit - Vi är bäst i Sverige på att designa, bygga och arbeta med
          moderna nätverk.
        </title>
      </Helmet>
      <TopBar />
      <main>
        <HomePage />
        <div className="divide-y divide-slate-200 dark:divide-white/5">
          <AboutPage />
          <OfferPage />
          <NaasPage />
          <CareerPage />
          <EmployeesPage />
          <ContactPage />
        </div>
      </main>
      <Footer1 />
    </>
  );
}

export default App;
